/** @format */

import React, { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Shake } from "../../..";
import { Counter, Note } from "./input.styles";

interface CharacterCounterProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  value?: string | number;
  minLength?: number;
  maxLength?: number;
  note?: string;
  error?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  size?: "lg" | "md" | "sm";
}

const CharacterCounter: FC<CharacterCounterProps> = ({
  value,
  minLength,
  maxLength,
  note,
  error,
  size,
  inputRef,
  ...props
}) => {
  const { t } = useTranslation();
  const [count, setCount] = React.useState(0);
  const [triggerMaxLengthShake, setTriggerMaxLengthShake] =
    React.useState(false);
  const [triggerErrorShake, setTriggerErrorShake] = React.useState(false);
  useEffect(() => {
    setCount((value || "").toString().length);
  }, [value]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        maxLength !== undefined &&
        String((e.target as HTMLInputElement | HTMLTextAreaElement)?.value)
          .length >= maxLength
      ) {
        setTriggerMaxLengthShake((e) => !e);
      }
      if (error) {
        setTriggerErrorShake((e) => !e);
      }
    };
    inputRef?.current?.addEventListener("keyup", handler);
    return () => {
      inputRef?.current?.removeEventListener("keyup", handler);
    };
  }, [inputRef, maxLength, error]);
  return note || minLength || maxLength ? (
    <Note
      error={error || count > (maxLength || Number.POSITIVE_INFINITY)}
      size={size}
      {...props}
    >
      <Shake
        trigger={triggerErrorShake}
        className="flex justify-between w-full"
      >
        {note}
      </Shake>

      <Shake
        trigger={triggerMaxLengthShake}
        className="flex justify-between w-full"
      >
        {(minLength || maxLength) && (
          <Counter>
            {count === 0 && minLength
              ? `${t("global:min").toLocaleLowerCase()}. ${minLength} ${t(
                  "global:characters",
                )}`
              : (!minLength && maxLength) ||
                  (minLength && count >= minLength && maxLength)
                ? `${count} / ${maxLength} ${t("global:characters")}`
                : minLength && count < minLength
                  ? `${count} / ${minLength} ${t("global:characters")}`
                  : null}
          </Counter>
        )}
      </Shake>
    </Note>
  ) : null;
};

export default CharacterCounter;
